import React, { useState } from 'react';

import { GlobalAccount, GUID } from '@ndvr/customer-api';
import { Account, ValidationResult } from '@ndvr/portfolio-api';

import { useCloseAccount } from 'views/portfolio/account-queries';
import { Button, ButtonsContainer, Modal, ModalContent, ModalText, VerticalLayout } from 'widgets';
import { ValidationResultModal } from 'widgets/delete/ValidationResultModal';

export const CloseAccountValidationModal = ({
    householdGUID,
    account,
    onClose,
}: {
    householdGUID: GUID<GlobalAccount>;
    account: Account;
    onClose: () => void;
}) => {
    const { mutateAsync: closeAccount, isPending } = useCloseAccount(householdGUID);
    const [validationResult, setValidationResult] = useState<ValidationResult>();

    return validationResult ? (
        <ValidationResultModal
            result={validationResult}
            validResultMessage={`${account.accountName.plainText} was successfully closed`}
            invalidResultMessage={`${account.accountName.plainText} cannot be closed`}
            onClose={() => {
                setValidationResult(undefined);
                onClose();
            }}
        />
    ) : (
        <CloseAccountConfirmModal
            account={account}
            disabled={isPending}
            onCloseAccount={async () => {
                const result = await closeAccount(account);
                setValidationResult(result);
            }}
            onCloseModal={onClose}
        />
    );
};

const CloseAccountConfirmModal = ({
    account,
    disabled,
    onCloseAccount,
    onCloseModal,
}: {
    account: Account;
    disabled: boolean;
    onCloseAccount: () => void;
    onCloseModal: () => void;
}) => {
    return (
        <Modal onClose={onCloseModal} modalTitle="Close account">
            <ModalContent>
                <VerticalLayout $gap={32}>
                    <ModalText>
                        Are you sure you want to close <b>{account.accountName.plainText}</b>?
                    </ModalText>
                    <ModalText>
                        Closing this Claro account will unlink it from the {account.custodyType} custody account{' '}
                        {account.custodyAccountId}. Once closed, this Claro account cannot be linked to an other custody
                        account.
                    </ModalText>
                </VerticalLayout>
            </ModalContent>
            <ButtonsContainer $gap={16}>
                <Button buttonType="secondary" onClick={onCloseModal}>
                    Cancel
                </Button>
                <Button buttonType="destructive" onClick={onCloseAccount} disabled={disabled}>
                    Close account
                </Button>
            </ButtonsContainer>
        </Modal>
    );
};
